import React, { useState } from "react";
import axios from "axios";
import { useApp } from "../store";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { Check } from "lucide-react";

export default function Inquiry() {
  useDocumentMeta({
    title: "B2B Inquiry — Bulk & Private Label",
    description: "Bulk pricing for 1kg+ orders, private-label distillations, custom blends for indie perfumers, cosmetic brands, and luxury houses worldwide. COA, GC-MS, IFRA, MSDS supplied.",
  });
  const { API } = useApp();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", product_name: "", quantity: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="inquiry-success">
        <div className="w-16 h-16 rounded-full bg-copper/10 mx-auto flex items-center justify-center mb-8">
          <Check size={32} className="text-copper" strokeWidth={1.5} />
        </div>
        <div className="overline text-copper mb-4">Inquiry Received</div>
        <h1 className="font-serif-display text-5xl mb-6">We will be in touch.</h1>
        <p className="text-[#5C5E5C]">Our trade team responds within 24 hours, often sooner. For urgent requests, please write to <span className="text-copper">hello@floralabsolutes.com</span>.</p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="inquiry-page">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="overline text-copper mb-4">For The Trade</div>
          <h1 className="headline-xl text-[clamp(3rem,6vw,6rem)] mb-8">
            B2B<br/><span className="italic text-[#8A8D8A]">inquiry.</span>
          </h1>
          <p className="text-[#5C5E5C] leading-relaxed mb-8">
            Bulk pricing for 1kg+ orders, private-label distillations, custom blends, and ongoing supply for indie perfumers, cosmetic brands, and luxury houses worldwide.
          </p>
          <div className="border-l-2 border-copper pl-6 text-sm space-y-3 text-[#5C5E5C]">
            <p><strong className="text-[#1E1F1E]">Documentation:</strong> COA, GC-MS, IFRA, MSDS supplied.</p>
            <p><strong className="text-[#1E1F1E]">Shipping:</strong> Worldwide via DHL/FedEx. CITES permits arranged for Oud.</p>
            <p><strong className="text-[#1E1F1E]">MOQ:</strong> 1kg per essence for B2B pricing.</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-8">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full name *" value={form.name} onChange={v => set("name", v)} testid="iq-name" required />
              <Field label="Company / Brand" value={form.company} onChange={v => set("company", v)} testid="iq-company" />
              <Field label="Email *" type="email" value={form.email} onChange={v => set("email", v)} testid="iq-email" required />
              <Field label="Phone (with country code)" value={form.phone} onChange={v => set("phone", v)} testid="iq-phone" />
              <Field label="Product of interest" value={form.product_name} onChange={v => set("product_name", v)} testid="iq-product" />
              <Field label="Quantity required" value={form.quantity} onChange={v => set("quantity", v)} testid="iq-quantity" />
            </div>
            <div>
              <label className="overline text-[10px] block mb-2">Message *</label>
              <textarea
                value={form.message} onChange={e => set("message", e.target.value)} required
                rows={6} className="input-underline resize-none" placeholder="Tell us about your project, brand, intended use, timeline…"
                data-testid="iq-message"
              />
            </div>
            {err && <div className="text-xs text-[#9E3C3C]">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary" data-testid="iq-submit">
              {busy ? "Sending…" : "Send inquiry"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
function Field({ label, value, onChange, testid, required, type = "text" }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}</label>
      <input type={type} className="input-underline" value={value} onChange={e => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
